import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaComments, FaChartPie, FaMobileAlt, FaRobot, FaUsers, FaArrowRight, FaBullhorn, FaBroadcastTower, FaReplyAll } from 'react-icons/fa';
import bulkWhatsappImage from '../../assets/bulk-whatsapp.png';

const BulkWhatsappPage = () => {
    // Pricing Toggle
    const [monthly, setMonthly] = useState(true);

    return (
        <div className="bg-white font-sans text-slate-900 pt-20">
            {/* 1. Main Intro Section (No Hero BG, Colorful Content) */}
            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">

                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-block px-4 py-1.5 bg-green-100 text-green-700 font-bold text-xs uppercase tracking-wider rounded-full mb-6">
                            Marketing Automation
                        </div>
                        <h1 className="text-4xl md:text-6xl font-extrabold mb-6 text-slate-900 leading-tight">
                            Unlock the Power of <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-700">Conversational Marketing</span>
                        </h1>
                        <p className="text-lg text-slate-600 mb-8 leading-relaxed font-medium">
                            Don't just send SMS. Start conversations at scale. With a 98% open rate, WhatsApp is the most effective channel to reach your audience instantly.
                        </p>

                        <div className="grid grid-cols-2 gap-4 mb-8">
                            {[
                                "98% Open Rates",
                                "45-60% Click Rates",
                                "Instant Delivery",
                                "Rich Media Support"
                            ].map((stat, idx) => (
                                <div key={idx} className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg border border-slate-100">
                                    <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-bold">✓</div>
                                    <span className="text-slate-800 font-bold text-sm">{stat}</span>
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-4">
                            <a href="#demo" className="px-8 py-4 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-all shadow-lg hover:shadow-green-500/30 flex items-center gap-2 transform hover:-translate-y-1">
                                Start Your Campaign <FaArrowRight className="text-sm" />
                            </a>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="relative"
                    >
                        <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-3xl p-8 shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-500 border border-green-200">
                            <img
                                src={bulkWhatsappImage}
                                alt="Bulk WhatsApp Dashboard"
                                className="w-full h-auto rounded-xl shadow-lg border border-white/50"
                            />
                        </div>
                        {/* Decorative Elements */}
                        <div className="absolute -z-10 -bottom-10 -left-10 w-48 h-48 bg-green-200/40 rounded-full blur-3xl animate-pulse"></div>
                        <div className="absolute -z-10 -top-10 -right-10 w-48 h-48 bg-emerald-200/40 rounded-full blur-3xl animate-pulse"></div>
                    </motion.div>
                </div>
            </div>

            {/* 2. Use Cases Section (Colorful Cards) */}
            <div className="bg-slate-50 py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">One Platform, Infinite Possibilities</h2>
                        <p className="text-slate-600 max-w-2xl mx-auto text-lg">Whether for sales, support, or alerts, reach your customers where they are.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { icon: FaBullhorn, title: "Promotions & Offers", desc: "Send personalized discount codes and flash sale alerts.", color: "text-rose-500", bg: "bg-rose-50", border: "border-rose-100" },
                            { icon: FaBroadcastTower, title: "Transactional Alerts", desc: "Order confirmations, shipping updates, and OTPs.", color: "text-blue-500", bg: "bg-blue-50", border: "border-blue-100" },
                            { icon: FaReplyAll, title: "Customer Support", desc: "Automate FAQs and provide instant support via chat.", color: "text-amber-500", bg: "bg-amber-50", border: "border-amber-100" },
                            { icon: FaUsers, title: "Event Reminders", desc: "Boost attendance with timely webinar and event nudges.", color: "text-purple-500", bg: "bg-purple-50", border: "border-purple-100" },
                            { icon: FaChartPie, title: "Feedback Surveys", desc: "Collect customer feedback with quick, interactive surveys.", color: "text-cyan-500", bg: "bg-cyan-50", border: "border-cyan-100" },
                            { icon: FaRobot, title: "Lead Qualification", desc: "Use chatbots to qualify leads before routing to sales.", color: "text-emerald-500", bg: "bg-emerald-50", border: "border-emerald-100" }
                        ].map((item, idx) => (
                            <div key={idx} className={`bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border ${item.border} group`}>
                                <div className={`w-14 h-14 ${item.bg} rounded-xl flex items-center justify-center mb-6`}>
                                    <item.icon className={`${item.color} text-2xl group-hover:scale-110 transition-transform`} />
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-slate-900">{item.title}</h3>
                                <p className="text-slate-600 text-sm leading-relaxed font-medium">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 3. Feature Deep Dive (High Visibility Light Section) */}
            <div className="py-24 bg-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(#22c55e 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>

                <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 text-slate-900">Designed for Growth</h2>
                        <p className="text-slate-600 max-w-2xl mx-auto text-lg font-medium">Powerful tools to manage millions of conversations without chaos.</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12">
                        <div className="bg-slate-50 border border-slate-200 p-8 rounded-2xl hover:shadow-lg transition-shadow">
                            <div className="flex items-start gap-4">
                                <FaMobileAlt className="text-4xl text-green-600 mt-1" />
                                <div>
                                    <h3 className="text-2xl font-bold mb-3 text-slate-900">Multi-Agent Inbox</h3>
                                    <p className="text-slate-600 leading-relaxed font-medium">Let your entire team manage customer queries from a single number. Assign chats, add internal notes, and never miss a follow-up.</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 p-8 rounded-2xl hover:shadow-lg transition-shadow">
                            <div className="flex items-start gap-4">
                                <FaRobot className="text-4xl text-green-600 mt-1" />
                                <div>
                                    <h3 className="text-2xl font-bold mb-3 text-slate-900">No-Code Chatbot Builder</h3>
                                    <p className="text-slate-600 leading-relaxed font-medium">Build sophisticated flow-based chatbots in minutes using our drag-and-drop editor. Automate sales and support without writing code.</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 p-8 rounded-2xl hover:shadow-lg transition-shadow">
                            <div className="flex items-start gap-4">
                                <FaChartPie className="text-4xl text-green-600 mt-1" />
                                <div>
                                    <h3 className="text-2xl font-bold mb-3 text-slate-900">Deep Analytics</h3>
                                    <p className="text-slate-600 leading-relaxed font-medium">Track every message sent, delivered, and read. Analyze campaign ROI and optimize your messaging strategy based on real data.</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 p-8 rounded-2xl hover:shadow-lg transition-shadow">
                            <div className="flex items-start gap-4">
                                <FaWhatsapp className="text-4xl text-green-600 mt-1" />
                                <div>
                                    <h3 className="text-2xl font-bold mb-3 text-slate-900">Official Green Tick</h3>
                                    <p className="text-slate-600 leading-relaxed font-medium">As an official WhatsApp BSP partner, we help you get Verified Business status (Green Tick) to build immediate trust.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 4. Contact / Pricing CTA */}
            <div id="demo" className="py-24 bg-white">
                <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
                    <h2 className="text-4xl font-bold mb-6 text-slate-900">Ready to Skyrocket Your Engagement?</h2>
                    <p className="text-lg text-slate-600 mb-12 max-w-2xl mx-auto font-medium">Be where your customers are. Start your 14-day free trial on the world's most popular messaging app.</p>

                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-1 rounded-3xl max-w-lg mx-auto shadow-2xl">
                        <div className="bg-white rounded-[20px] p-8 md:p-12">
                            <div className="mb-8">
                                <span className="text-slate-500 font-bold uppercase tracking-wider text-sm">Starter Plan</span>
                                <div className="text-5xl font-extrabold text-slate-900 mt-4 mb-2">₹999<span className="text-lg text-slate-400 font-medium">/mo</span></div>
                                <p className="text-slate-500">Perfect for growing businesses</p>
                            </div>
                            <ul className="text-left space-y-4 mb-8 pl-4">
                                <li className="flex items-center gap-3 text-slate-700 font-medium"><FaWhatsapp className="text-green-500" /> 10,000 Messages/mo</li>
                                <li className="flex items-center gap-3 text-slate-700 font-medium"><FaWhatsapp className="text-green-500" /> 5 Team Members</li>
                                <li className="flex items-center gap-3 text-slate-700 font-medium"><FaWhatsapp className="text-green-500" /> Chatbot Builder</li>
                                <li className="flex items-center gap-3 text-slate-700 font-medium"><FaWhatsapp className="text-green-500" /> Priority Support</li>
                            </ul>
                            <button className="w-full py-4 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition-colors shadow-lg text-lg uppercase tracking-wide">
                                Start Free Trial
                            </button>
                            <p className="text-xs text-slate-400 mt-4 font-medium">No credit card required • Cancel anytime</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BulkWhatsappPage;
